"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Shield, FileText, AlertTriangle, Users } from 'lucide-react';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-slate-950">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link href="/register" className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Kayıt Sayfasına Dön
          </Link>

          <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 rounded-xl p-8 border border-blue-500/20">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <FileText className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-100">Kullanım Koşulları</h1>
                <p className="text-slate-300 mt-2">AXTROLOG Kullanıcı Sözleşmesi</p>
              </div>
            </div>

            <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4 mt-6">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-5 w-5 text-amber-400" />
                <span className="font-semibold text-amber-200">Önemli Uyarı</span>
              </div>
              <p className="text-amber-100 text-sm">
                Son güncelleme: {new Date().toLocaleDateString('tr-TR')}.
                Bu sözleşmeyi kabul ederek, astroloji hizmetlerinin bilgilendirme amaçlı olduğunu ve bilimsel kesinlik taşımadığını kabul etmiş sayılırsınız.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-slate-900 rounded-xl p-8 border border-slate-700 space-y-8">

          {/* Section 1: Taraflar ve Tanımlar */}
          <section>
            <h2 className="text-xl font-bold text-slate-100 mb-4 flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-400" />
              1. TARAFLAR VE TANIMLAR
            </h2>
            <div className="text-slate-300 space-y-4">
              <p><strong>AXTROLOG:</strong> Bu sözleşmede "Platform", "Uygulama", "Hizmet" olarak anılacak astroloji ve tarot danışmanlık hizmetleri sunan internet platformudur.</p>
              <p><strong>KULLANICI:</strong> AXTROLOG platformunu kullanan, 18 yaş üstü, fiil ehliyetine sahip gerçek kişilerdir.</p>
              <p><strong>HİZMET:</strong> Astroloji harita çıkarma, tarot okuma, kişisel danışmanlık ve ilgili eğitim içeriklerini kapsar.</p>
              <p><strong>İÇERİK:</strong> Platform üzerindeki tüm yazılı, görsel, sesli materyaller ve analiz sonuçlarını ifade eder.</p>
            </div>
          </section>

          {/* Section 2: Hizmetin Niteliği */}
          <section>
            <h2 className="text-xl font-bold text-slate-100 mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5 text-purple-400" />
              2. HİZMETİN NİTELİĞİ VE SINIRLAMALARI
            </h2>
            <div className="text-slate-300 space-y-4">
              <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
                <h3 className="font-semibold text-red-200 mb-2">⚠️ Kritik Bilgilendirmeler:</h3>
                <ul className="list-disc pl-6 space-y-2 text-red-100">
                  <li>Astroloji ve tarot <strong>bilim dalı değildir</strong> ve bilimsel kesinlik iddiası taşımaz</li>
                  <li>Sunulan içerikler <strong>sadece bilgilendirme, eğlence ve kişisel farkındalık</strong> amaçlıdır</li>
                  <li>Hiçbir analiz sonucu <strong>kesin öngörü veya garantili tahmin</strong> içermez</li>
                  <li>Sağlık, hukuki, finansal kararlar için <strong>mutlaka uzman görüşü</strong> alınmalıdır</li>
                </ul>
              </div>

              <p>AXTROLOG, kullanıcıların kişisel gelişim ve öz-farkındalık süreçlerine destek olmayı amaçlar. Tüm hizmetler, Türkiye Cumhuriyeti kanunları çerçevesinde, <strong>6502 sayılı Tüketicinin Korunması Hakkında Kanun</strong> ve <strong>6563 sayılı Elektronik Ticaretin Düzenlenmesi Hakkında Kanun</strong> kapsamında sunulmaktadır.</p>
            </div>
          </section>

          {/* Section 3: Kullanıcı Yükümlülükleri */}
          <section>
            <h2 className="text-xl font-bold text-slate-100 mb-4">3. KULLANICI YÜKÜMLÜLÜKLERİ</h2>
            <div className="text-slate-300 space-y-4">
              <h3 className="font-semibold text-slate-200">3.1 Genel Yükümlülükler:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Kayıt sırasında doğru, güncel ve eksiksiz bilgi vermek</li>
                <li>Hesap güvenliğini sağlamak ve şifre bilgilerini korumak</li>
                <li>Platformu sadece yasal amaçlar için kullanmak</li>
                <li>Diğer kullanıcıların haklarına saygı göstermek</li>
                <li>Telif hakları ve fikri mülkiyet haklarını ihlal etmemek</li>
              </ul>

              <h3 className="font-semibold text-slate-200 mt-6">3.2 Yasaklanan Davranışlar:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Platform güvenliğini tehlikeye atabilecek yazılım/kod kullanmak</li>
                <li>Sistemi kötüye kullanmak veya zarar vermeye çalışmak</li>
                <li>Hakaret, küfür, pornografi içeren içerik paylaşmak</li>
                <li>Ticari amaçlı spam veya reklam göndermek</li>
                <li>Başkalarının kişisel bilgilerini izinsiz paylaşmak</li>
              </ul>
            </div>
          </section>

          {/* Section 4: Platform Yükümlülükleri */}
          <section>
            <h2 className="text-xl font-bold text-slate-100 mb-4">4. PLATFORM YÜKÜMLÜLÜKLERİ VE SINIRLAMALARI</h2>
            <div className="text-slate-300 space-y-4">
              <h3 className="font-semibold text-slate-200">4.1 Hizmet Sunumu:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Hizmetleri mümkün olan en iyi kalitede sunmaya çalışmak</li>
                <li>Kullanıcı verilerini mevzuat gereği korumak</li>
                <li>Platform güvenliğini sağlamaya çalışmak</li>
                <li>Güncellemeler hakkında bilgilendirme yapmak</li>
              </ul>

              <h3 className="font-semibold text-slate-200 mt-6">4.2 Sorumluluk Sınırlamaları:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>AXTROLOG, astroloji/tarot analizlerinin doğruluğunu garanti etmez</li>
                <li>Kullanıcıların bu analizlere dayanarak aldığı kararlardan sorumlu değildir</li>
                <li>Teknik arızalar, internet kesintileri gibi kontrolü dışındaki durumlardan sorumlu değildir</li>
                <li>Üçüncü taraf hizmet sağlayıcılarının sebep olduğu zararlardan sorumlu değildir</li>
              </ul>
            </div>
          </section>

          {/* Section 5: Kişisel Verilerin Korunması */}
          <section>
            <h2 className="text-xl font-bold text-slate-100 mb-4">5. KİŞİSEL VERİLERİN KORUNMASI</h2>
            <div className="text-slate-300 space-y-4">
              <p><strong>6698 sayılı Kişisel Verilerin Korunması Kanunu</strong> kapsamında:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Doğum bilgileri ve kişisel veriler sadece hizmet sunumu için işlenir</li>
                <li>Veriler üçüncü şahıslarla paylaşılmaz (yasal zorunluluk hariç)</li>
                <li>Kullanıcı istediği zaman verilerinin silinmesini talep edebilir</li>
                <li>Veri güvenliği için teknik ve idari tedbirler alınır</li>
                <li>Detaylı bilgi için <Link href="/gizlilik-politikasi" className="text-blue-400 hover:text-blue-300 underline">Gizlilik Politikası</Link>nı inceleyiniz</li>
              </ul>
            </div>
          </section>

          {/* Section 6: Ödeme ve İptal */}
          <section>
            <h2 className="text-xl font-bold text-slate-100 mb-4">6. ÖDEME, İADE VE İPTAL KOŞULLARI</h2>
            <div className="text-slate-300 space-y-4">
              <h3 className="font-semibold text-slate-200">6.1 Ödeme:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Tüm ödemeler TL (Türk Lirası) üzerinden yapılır</li>
                <li>Fiyatlar KDV dahildir</li>
                <li>Ödeme güvenliği SSL sertifikası ile korunur</li>
              </ul>

              <h3 className="font-semibold text-slate-200 mt-6">6.2 Cayma Hakkı (6502 SK m.48):</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Dijital içerik hizmetlerinde cayma hakkı <strong>hizmet sunumu başladığında</strong> sona erer</li>
                <li>Eğitim programları için 14 gün cayma hakkı vardır</li>
                <li>Cayma hakkı kullanıldığında iade 10 iş günü içinde yapılır</li>
              </ul>
            </div>
          </section>

          {/* Section 7: Fikri Mülkiyet */}
          <section>
            <h2 className="text-xl font-bold text-slate-100 mb-4">7. FİKRİ MÜLKİYET HAKLARI</h2>
            <div className="text-slate-300 space-y-4">
              <ul className="list-disc pl-6 space-y-2">
                <li>Platform üzerindeki tüm içerik AXTROLOG'un fikri mülkiyetidir</li>
                <li><strong>5846 sayılı Fikir ve Sanat Eserleri Kanunu</strong> kapsamında korunur</li>
                <li>İzinsiz kopyalama, çoğaltma, dağıtım yasaktır</li>
                <li>Kullanıcı sadece kişisel kullanım hakkına sahiptir</li>
                <li>İhlal durumunda yasal işlem başlatılır</li>
              </ul>
            </div>
          </section>

          {/* Section 8: Sözleşmenin Sona Ermesi */}
          <section>
            <h2 className="text-xl font-bold text-slate-100 mb-4">8. SÖZLEŞMENİN SONA ERMESİ</h2>
            <div className="text-slate-300 space-y-4">
              <h3 className="font-semibold text-slate-200">8.1 Kullanıcı Tarafından:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Hesabını dilediği zaman kapatabilir</li>
                <li>Kişisel verilerin silinmesini talep edebilir</li>
                <li>Devam eden hizmetler tamamlanır</li>
              </ul>

              <h3 className="font-semibold text-slate-200 mt-6">8.2 Platform Tarafından:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Sözleşme ihlali durumunda hesap kapatılabilir</li>
                <li>Yasal yükümlülük durumunda erişim engellenebilir</li>
                <li>30 gün önceden bildirimle hizmet sonlandırılabilir</li>
              </ul>
            </div>
          </section>

          {/* Section 9: Uyuşmazlık Çözümü */}
          <section>
            <h2 className="text-xl font-bold text-slate-100 mb-4">9. UYUŞMAZLIK ÇÖZÜMÜ</h2>
            <div className="text-slate-300 space-y-4">
              <ul className="list-disc pl-6 space-y-2">
                <li>Öncelikle müşteri hizmetleri ile çözüm aranır</li>
                <li>Tüketici sorunları için <strong>Tüketici Hakem Heyetleri</strong>ne başvuru yapılabilir</li>
                <li>Uyuşmazlıklarda <strong>İstanbul Mahkemeleri ve İcra Müdürlükleri</strong> yetkilidir</li>
                <li>Türkiye Cumhuriyeti kanunları uygulanır</li>
              </ul>
            </div>
          </section>

          {/* Section 10: Değişiklikler */}
          <section>
            <h2 className="text-xl font-bold text-slate-100 mb-4">10. SÖZLEŞME DEĞİŞİKLİKLERİ</h2>
            <div className="text-slate-300 space-y-4">
              <ul className="list-disc pl-6 space-y-2">
                <li>AXTROLOG bu sözleşmeyi değiştirme hakkını saklı tutar</li>
                <li>Önemli değişiklikler 30 gün önceden bildirilir</li>
                <li>Değişiklikleri kabul etmeyenler hizmet kullanımını durdurabilir</li>
                <li>Platform kullanımının devamı değişikliklerin kabulü anlamına gelir</li>
              </ul>
            </div>
          </section>

          {/* Contact Information */}
          <section className="bg-slate-800 rounded-lg p-6 border border-slate-600">
            <h2 className="text-xl font-bold text-slate-100 mb-4">İLETİŞİM BİLGİLERİ</h2>
            <div className="text-slate-300 space-y-2">
              <p><strong>AXTROLOG</strong></p>
              <p>E-posta: info@axtrolog.com</p>
              <p>Müşteri Hizmetleri: 0850 xxx xx xx</p>
              <p>Adres: [Şirket adresi buraya eklenecek]</p>
              <p>Çalışma Saatleri: Pazartesi-Cuma 09:00-18:00</p>
            </div>
          </section>

          {/* Final Notice */}
          <section className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 text-blue-400 mt-1" />
              <div>
                <h3 className="font-semibold text-blue-200 mb-2">Son Hatırlatma</h3>
                <p className="text-blue-100 text-sm leading-relaxed">
                  Bu kullanım koşullarını kabul ederek, astroloji ve tarot hizmetlerinin <strong>bilgilendirme ve eğlence amaçlı</strong> olduğunu,
                  yaşamsal kararlar için mutlaka uzman desteği alacağınızı beyan etmiş sayılırsınız.
                  AXTROLOG, sunduğu hizmetlerle kullanıcıların kişisel gelişimine destek olmayı amaçlar ancak
                  hiçbir analiz sonucunun kesin öngörü içermediğini hatırlatır.
                </p>
              </div>
            </div>
          </section>

        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <Link
            href="/register"
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all transform hover:scale-105 shadow-lg"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Kayıt Sayfasına Dön
          </Link>
        </div>
      </div>
    </div>
  );
}